import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ImportPODPage from './pages/ImportPODPage';
import PODActivePage from './pages/PODActivePage';
import SettingsPage from './pages/SettingsPage';
import BrandDocumentsPage from './pages/BrandDocumentsPage';
import CommunicationsPage from './pages/CommunicationsPage';
import ImportBillingPage from './pages/ImportBillingPage';
import ImportRCUPage from './pages/ImportRCUPage';
import ProvincesActivePage from './pages/ProvincesActivePage';
import ProvincesClosedPage from './pages/ProvincesClosedPage';
import CommunicationsPendingPage from './pages/CommunicationsPendingPage';
import CommunicationsSentPage from './pages/CommunicationsSentPage';
import LeadsPage from './pages/LeadsPage';
import PreContractsPage from './pages/PreContractsPage';
import MarketingGroupsPage from './pages/MarketingGroupsPage';
import MarketingCampaignsPage from './pages/MarketingCampaignsPage';
import MarketingCommunicationsPage from './pages/MarketingCommunicationsPage';
import SignPage from './pages/SignPage';
import OffersPage from './pages/OffersPage';
import FattureOcrPage from './pages/FattureOcrPage';
import LeadDetailPage from './pages/LeadDetailPage';
import ContractsPage from './pages/ContractsPage';
import FunnelDashboardPage from './pages/FunnelDashboardPage';
import SalesKitPage from './pages/SalesKitPage';
import OfferEditorPage from './pages/OfferEditorPage';
import CompliancePage from './pages/CompliancePage';
import CustomersListPage from './pages/CustomersListPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import PodDetailPage from './pages/PodDetailPage';
import KnowledgePage from './pages/KnowledgePage';
import UsersPage from './pages/UsersPage';
import ComplianceReportsPage from './pages/ComplianceReportsPage';
import ComplianceSettingsPage from './pages/ComplianceSettingsPage';
import ApprovalsPage from './pages/ApprovalsPage';
import CreditCheckPage from './pages/CreditCheckPage';

// Placeholder pages
const PlaceholderPage = ({ title, description }) => (
  <div className="max-w-4xl mx-auto text-center py-20">
    <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900 mb-4">{title}</h1>
    <p className="text-slate-600">{description}</p>
    <p className="text-sm text-slate-500 mt-4">Funzionalità in sviluppo</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sign/:token" element={<SignPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />
            
            {/* Clienti */}
            <Route path="leads" element={<LeadsPage />} />
            <Route path="pre-contracts" element={<PreContractsPage />} />
            
            {/* Dogane - Import */}
            <Route path="pods/import" element={<ImportPODPage />} />
            <Route path="rcu/import" element={<ImportRCUPage />} />
            <Route path="billing/import" element={<ImportBillingPage />} />
            
            {/* Dogane - Gestione POD */}
            <Route path="pods/active" element={<PODActivePage />} />
            
            {/* Dogane - Province */}
            <Route path="provinces/active" element={<ProvincesActivePage />} />
            <Route path="provinces/closed" element={<ProvincesClosedPage />} />
            
            {/* Dogane - Comunicazioni */}
            <Route path="communications/pending" element={<CommunicationsPendingPage />} />
            <Route path="communications/sent" element={<CommunicationsSentPage />} />
            <Route path="communications/provinces" element={<CommunicationsPage />} />
            
            {/* Pagamenti */}
            <Route path="payments" element={<PlaceholderPage title="Versamenti F24" description="Gestione versamenti accise" />} />
            <Route path="payments/generate" element={<PlaceholderPage title="Genera F24" description="Genera modello F24 precompilato" />} />
            <Route path="payments/history" element={<PlaceholderPage title="Storico Pagamenti" description="Cronologia versamenti effettuati" />} />
            
            {/* Marketing */}
            <Route path="marketing/groups" element={<MarketingGroupsPage />} />
            <Route path="marketing/campaigns" element={<MarketingCampaignsPage />} />
            <Route path="marketing/communications" element={<MarketingCommunicationsPage />} />

            {/* Commerciale */}
            <Route path="offers" element={<OffersPage />} />
            <Route path="offers/:offerId/edit" element={<OfferEditorPage />} />
            <Route path="fatture-ocr" element={<FattureOcrPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="credito" element={<Navigate to="/credito/portafoglio" replace />} />
            <Route path="credito/:section" element={<CreditCheckPage />} />
            <Route path="leads/:leadId" element={<LeadDetailPage />} />
            <Route path="contracts" element={<ContractsPage />} />
            <Route path="funnel" element={<FunnelDashboardPage />} />
            <Route path="folder" element={<SalesKitPage />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="compliance/reports" element={<ComplianceReportsPage />} />
            <Route path="compliance/settings" element={<ComplianceSettingsPage />} />
            <Route path="compliance/dogane" element={<PlaceholderPage title="Compliance Dogane" description="Compliance accise UADM, ADM, F24, dichiarazioni mensili/semestrali/annuali. In sviluppo." />} />
            <Route path="compliance/acquirente-unico" element={<PlaceholderPage title="Acquirente Unico" description="Adempimenti verso AU: scambi dati, switching, TIRV, monitoraggio. In sviluppo." />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="customers" element={<CustomersListPage />} />
            <Route path="customers/:customerId" element={<CustomerDetailPage />} />
            <Route path="pods/:leadId/:podIndex" element={<PodDetailPage />} />
            
            {/* Dichiarazioni */}
            <Route path="declarations" element={<PlaceholderPage title="Dichiarazioni" description="Mensili, Semestrali, Annuali" />} />
            
            {/* Settings */}
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/brand" element={<BrandDocumentsPage />} />
            <Route path="settings/users" element={<UsersPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" expand={false} richColors />
    </AuthProvider>
  );
}

export default App;
